import { useRouter } from 'next/router'
import React, { useMemo, useState } from 'react'

import { useDispatch } from 'react-redux'
import { change as formChange } from 'redux-form'

import { Grid, Link } from '@material-ui/core'
import Chip, { ChipProps } from '@material-ui/core/Chip'
import { makeStyles, createStyles, Theme } from '@material-ui/core/styles'
import ClearIcon from '@material-ui/icons/Clear'

import Flexbox from '~/components/shared/Flexbox'
import Text from '~/components/shared/Text'

import Paths from '~/utils/paths'

import { sellerTypeOptions } from '~/data/options'

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    chips: {
      display: 'flex',
      flexWrap: 'wrap',
      '& > *': {
        margin: theme.spacing(0.5)
      }
    },
    chip: {
      color: theme.palette.text.primary,
      maxWidth: '100%'
    }
  })
)

const FilterChip = ({ label, onDelete }: ChipProps) => {
  const classes = useStyles()

  return (
    <Chip
      className={classes.chip}
      label={label}
      onDelete={onDelete}
      color="primary"
      variant="outlined"
      deleteIcon={<ClearIcon color="inherit" />}
    />
  )
}

interface SearchFiltersChipsProps {
  renderWrapper?: (params: { items: any[]; children: any }) => JSX.Element
  limitChips?: boolean
}

const SearchFiltersChips = ({
  renderWrapper,
  limitChips
}: SearchFiltersChipsProps) => {
  const router = useRouter()
  const classes = useStyles()
  const dispatch = useDispatch()
  const [seeAll, setSeeAll] = useState(false)

  const onClear = () => router.push('/search', undefined, { shallow: true })
  const change = (key, value) =>
    dispatch(formChange('Listings_Filters_Form', key, value))

  const items = useMemo(() => {
    const arrayFormat = Paths.search.formatSearch(router.asPath)
    const category = arrayFormat.find((el) => el.key === 'category')

    return arrayFormat.reduce((acc, param) => {
      if (param === category) return acc

      if (param.key === 'by_type' && category) {
        acc.push({
          ...param,
          formatValue: `${param.formatValue} | ${category.formatValue}`
        })
      } else if (!['page', 'sorting'].includes(param.key)) {
        acc.push(param)
      }

      return acc
    }, [])
  }, [router.asPath])

  const filterChipItemsMap = (item) => {
    const itemWithoutKey = ['by_type', 'by_condition']
    if (itemWithoutKey.includes(item.key)) {
      return (
        <FilterChip
          key={item.key}
          label={item.formatValue}
          title={item.formatValue}
          onDelete={() => {
            change(item.key, null)
          }}
        />
      )
    }

    if (item.key === 'by_seller_type') {
      const label = sellerTypeOptions.find((s) => s.value === item.value)?.label

      if (!label) {
        change(item.key, null)
        return null
      }

      return (
        <FilterChip
          key={item.key}
          label={label}
          title={label}
          onDelete={() => {
            change(item.key, null)
          }}
        />
      )
    }

    // if (Array.isArray(item.value)) {
    //   // @ts-ignore
    //   return value.map((newValue) => (
    //     <FilterChip
    //       key={newValue}
    //       label={newValue}
    //       onDelete={() => {
    //         change(
    //           item.key,
    //           // @ts-ignore
    //           item.value.filter((v) => v !== newValue)
    //         )
    //       }}
    //     />
    //   ))
    // }

    return (
      <FilterChip
        key={item.key}
        label={`${item.formatKey}: ${item.formatValue}`}
        title={`${item.formatKey}: ${item.formatValue}`}
        onDelete={() => {
          change(item.key, null)
        }}
      />
    )
  }

  const itemsAll = useMemo(() => {
    if (limitChips && items) {
      // items.forEach(() => {
      //   const font = "13px Nunito Sans, sans-serif";
      //   const canvas = document.createElement("canvas");
      //   const context = canvas.getContext("2d");
      //   context.font = font;
      //   const width = context.measureText(inputText).width;
      //   const formattedWidth = Math.ceil(width) + "px";
      // })

      const _reviews = [...items]
      return {
        preview: _reviews.splice(0, 3),
        other: _reviews
      }
    }

    return {}
  }, [items, limitChips])

  const filterChips = (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Flexbox justifyContent="space-between" mb={1}>
          <Text fontSize={14} m={0} component="h3" variant="h3">
            Your Filters:
          </Text>
          <Link
            role="button"
            type="button"
            component="button"
            underline="none"
            onClick={onClear}
          >
            Clear all
          </Link>
        </Flexbox>
        <Flexbox flexWrap="wrap" className={classes.chips}>
          {limitChips ? (
            <>
              {itemsAll.preview.map(filterChipItemsMap)}
              {seeAll && itemsAll.other.map(filterChipItemsMap)}
              {!seeAll && items.length > 3 && (
                <Link
                  type="button"
                  component="button"
                  underline="none"
                  onClick={() => setSeeAll(!seeAll)}
                  style={{ minHeight: 32 }}
                >
                  Show all
                </Link>
              )}
            </>
          ) : (
            <>{items.map(filterChipItemsMap)}</>
          )}
        </Flexbox>
      </Grid>
    </Grid>
  )

  if (renderWrapper) {
    return <>{renderWrapper({ items, children: filterChips })}</>
  }

  return <>{filterChips}</>
}

export default SearchFiltersChips
